import React from 'react';
import { styled } from '@this/constants/themes';
import { media } from '@this/components/shared/atoms/media';
import { buildDomesticAirTicketLabel, type TransitPriceJson } from '@this/domain/select_repository';
import { resolveDomesticAirFareDetail } from '@this/domain/domestic_air/fare_detail_master';
import DomesticAirFareTooltip, {
  SeatSelectionRestrictedNote
} from '@this/components/reserve_trip/select/transport_box/shared/domestic_air_fare_tooltip';
import { digits } from '@this/src/util/price/price.util';
import { Loading } from '@this/src/components/shared/ui/feedbacks/loading';

type Props = {
  loading: boolean;
  selected: boolean;
  price: TransitPriceJson;
  departureDate?: string | Date | null;
  flightNumber?: string | null;
  sources?: string[];
  fetchingExpiredPrice?: boolean;
  disabled?: boolean;
  isUnselectable?: boolean;
};

const SelectTransportBoxRightInDomesticAirListTemplate: React.FC<Props> = ({
  loading,
  selected,
  price,
  departureDate,
  flightNumber,
  sources,
  fetchingExpiredPrice,
  disabled,
  isUnselectable
}) => {
  const { ticketCategoryLabel, ticketTypeLabel, isKabu } = buildDomesticAirTicketLabel(
    price,
    departureDate,
    flightNumber
  );
  const fareDetail = resolveDomesticAirFareDetail({
    ticketType: price.ticket_type,
    seatType: price.seat_type,
    flightNumber: price.flight_number || flightNumber,
    departureDate
  });
  return (
    <RightDomesticAirButton
      selected={selected}
      priceExpired={price.price_expired}
      fetchingDisabled={fetchingExpiredPrice}
      className={disabled ? 'disabled' : undefined}
      style={disabled ? { cursor: 'default', pointerEvents: 'none' } : undefined}
    >
      {loading ? (
        <RightBody style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50px' }}>
          <img src="/images/loading.gif" width={20} height={20} />
        </RightBody>
      ) : disabled && !isUnselectable ? (
        <RightBody style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50px' }}>
          選択できません
        </RightBody>
      ) : (
        <RightBody className="select-transport-box__right__body">
          <TicketLabelWrapper>
            <TicketLabelContainer>
              <div>
                <TicketLabelArea>
                  <TicketLabel>{ticketCategoryLabel}</TicketLabel>
                  {price.seat_type && <TicketLabel>{price.seat_type}</TicketLabel>}
                </TicketLabelArea>
                <Price>
                  {price.price !== undefined && price.price !== null && (
                    <>
                      <span data-wovn-ignore>{digits(price.price)}</span>
                      <Yen>円</Yen>
                    </>
                  )}
                </Price>
                <PeopleNum>/1名</PeopleNum>
              </div>
              {(ticketTypeLabel || fareDetail) && (
                <div style={{ display: 'flex', alignItems: 'center', columnGap: '4px' }}>
                  {ticketTypeLabel && (
                    <FareBrandLabel selected={selected} isKabu={isKabu}>
                      {ticketTypeLabel}
                    </FareBrandLabel>
                  )}
                  {fareDetail && <DomesticAirFareTooltip detail={fareDetail} place="left" />}
                </div>
              )}
              {fareDetail?.seatSelectionRestricted && <SeatSelectionRestrictedNote />}
            </TicketLabelContainer>
            <SeatBox selected={selected} priceExpired={price.price_expired} isUnselectable={isUnselectable}>
              {isUnselectable ? (
                <div style={{ fontSize: '12px', textAlign: 'center' }}>選択できません</div>
              ) : sources?.includes('ana_official') ? (
                <>
                  <div style={{ fontSize: '12px' }}>
                    {price.seat_count === 'unknown'
                      ? '-'
                      : price.seat_count === '空席待ち'
                      ? '空席待ち'
                      : !Number.isNaN(Number(price.seat_count)) &&
                        price.seat_count !== '' &&
                        price.seat_count != null &&
                        Number(price.seat_count) < 10
                      ? '△'
                      : '○'}
                  </div>
                  {price.seat_count !== 'unknown' && price.seat_count !== '空席待ち' && (
                    <div>
                      {!Number.isNaN(Number(price.seat_count)) &&
                      price.seat_count !== '' &&
                      price.seat_count != null &&
                      Number(price.seat_count) < 10
                        ? `${price.seat_count}席`
                        : '10席以上'}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div style={{ fontSize: '12px' }}>○</div>
                  {price.seat_count !== 'unknown' && (
                    <div>
                      {price.seat_count === '○' || Number(price.seat_count) >= 10
                        ? '10席以上'
                        : `${price.seat_count}席`}
                    </div>
                  )}
                </>
              )}
              {!isUnselectable && fetchingExpiredPrice && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '2px' }}>
                  <Loading size="small" centerd={false} />
                  <span style={{ fontSize: '8px', whiteSpace: 'nowrap' }}>再取得中</span>
                </div>
              )}
            </SeatBox>
          </TicketLabelWrapper>
        </RightBody>
      )}
    </RightDomesticAirButton>
  );
};

const RightBody = styled.div`
  font-size: 11px;
`;

const SeatBox = styled.div<{ selected: boolean; priceExpired?: boolean; isUnselectable?: boolean }>`
  width: 120px;
  flex-shrink: 0;
  background: ${props => (props.isUnselectable ? 'transparent' : '#f9f7f3')};
  display: flex;
  font-size: 14px;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  ${props =>
    props.selected &&
    `
      background: ${props.theme.linkColor};
      color: white;
  `}
`;

const Price = styled.span`
  font-size: 16px;

  ${media.sp`
    font-size: 18px;
    font-weight: bold;
    line-height: 1;
  `}
`;

const PeopleNum = styled.span`
  font-size: 11px;
`;

const TicketLabelArea = styled.div`
  margin-bottom: 4px;
  display: flex;
  flex-wrap: wrap;
  row-gap: 2px;
  @media screen and (max-width: 768px) {
    line-height: 200%;
  }
`;

const TicketLabel = styled.span`
  border: solid 1px #af985e;
  margin-right: 5px;
  padding: 2px 8px;
  font-size: 12px;
  white-space: nowrap;
`;

const TicketLabelWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TicketLabelContainer = styled.div`
  padding: 10px;
`;

const FareBrandLabel = styled.span<{ selected: boolean; isKabu: boolean }>`
  font-size: 12px;
  font-weight: bold;
  ${props =>
    !props.selected &&
    (props.isKabu
      ? `
    color: red;
  `
      : `
    color: ${props.theme.linkColor};
  `)}
`;

const RightDomesticAirButton = styled.div<{
  selected: boolean;
  priceExpired?: boolean;
  fetchingDisabled?: boolean;
}>`
  border: 2px solid ${props => props.theme.linkColor};
  border-radius: 3px;
  color: ${props => props.theme.linkColor};
  font-weight: bold;
  cursor: pointer;
  background-color: #fff;
  &.flight {
    min-width: 116px;
  }
  &.disabled {
    border-color: ${props => props.theme.grayBorderColor};
    color: ${props => props.theme.grayTextColor};
  }
  ${props => props.selected && `background: ${props.theme.linkColor}; color: white;`}
`;

const Yen = styled.span`
  font-size: 11px;
`;

export default SelectTransportBoxRightInDomesticAirListTemplate;
